import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { normalizeListResponse } from '@/utils/apiResponseUtils'
import type {
  DiaChiNhanHangId,
  IDiaChiNhanHang,
  IDiaChiNhanHangAllQuery,
  IDiaChiNhanHangPayload,
  KhachHangId,
} from '@/interfaces/diaChiNhanHangInterfaces'

function prefix(khachHangId: KhachHangId) {
  return `/khach-hang/${khachHangId}/dia-chi-nhan-hang`
}

function toBool(v: unknown): boolean {
  if (typeof v === 'boolean') return v
  if (v === 1 || v === '1' || v === 'true') return true
  return false
}

export function mapDiaChiNhanHangFromApi(raw: Record<string, unknown>): IDiaChiNhanHang {
  return {
    id: String(raw.id ?? ''),
    tenNguoiNhan: String(raw.tenNguoiNhan ?? ''),
    dienThoai: String(raw.dienThoai ?? ''),
    diaChi: String(raw.diaChi ?? ''),
    tinhThanh: String(raw.tinhThanh ?? ''),
    phuongXa: String(raw.phuongXa ?? ''),
    placeId: raw.placeId != null ? String(raw.placeId) : null,
    viDo:
      raw.viDo != null && Number.isFinite(Number(raw.viDo)) ? Number(raw.viDo) : null,
    kinhDo:
      raw.kinhDo != null && Number.isFinite(Number(raw.kinhDo))
        ? Number(raw.kinhDo)
        : null,
    macDinh: toBool(raw.macDinh),
    ngayTao: raw.ngayTao != null ? String(raw.ngayTao) : null,
    ngayCapNhat: raw.ngayCapNhat != null ? String(raw.ngayCapNhat) : null,
  }
}

function toPayload(body: IDiaChiNhanHangPayload) {
  return {
    tenNguoiNhan: body.tenNguoiNhan.trim(),
    dienThoai: body.dienThoai.trim(),
    diaChi: body.diaChi.trim(),
    tinhThanh: body.tinhThanh.trim(),
    phuongXa: body.phuongXa.trim(),
    placeId: body.placeId ?? null,
    viDo: body.viDo ?? null,
    kinhDo: body.kinhDo ?? null,
    macDinh: Boolean(body.macDinh),
  }
}

export function fetchDanhSachDiaChiNhanHang(khachHangId: KhachHangId) {
  return apiRequest(
    ApisServiceApp.get(`${prefix(khachHangId)}/danh-sach-dia-chi-nhan-hang`).then((res: unknown) => {
      const { data, paging } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return {
        data: data.map((r) => mapDiaChiNhanHangFromApi(r as Record<string, unknown>)),
        paging,
      }
    }),
  )
}

export function fetchDanhSachTatCaDiaChiNhanHang(query: IDiaChiNhanHangAllQuery = {}) {
  const params: Record<string, string> = {}
  Object.entries(query).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return
    params[k] = String(v)
  })
  return apiRequest(
    ApisServiceApp.get('/dia-chi-nhan-hang/danh-sach-dia-chi-nhan-hang', { params }).then(
      (res: unknown) => {
        const { data, paging } = normalizeListResponse(
          res as Parameters<typeof normalizeListResponse>[0],
        )
        const rows = (data ?? []) as Record<string, unknown>[]
        const mapped = rows.map((r) => {
          const item = mapDiaChiNhanHangFromApi(r)
          const kh = r.khachHang as Record<string, unknown> | undefined
          if (kh) {
            item.khachHang = {
              id: String(kh.id ?? ''),
              maKhachHang: String(kh.maKhachHang ?? ''),
              tenKhachHang: String(kh.tenKhachHang ?? ''),
            }
          }
          return item
        })
        return { data: mapped, paging }
      },
    ),
  )
}

export function taoDiaChiNhanHang(khachHangId: KhachHangId, body: IDiaChiNhanHangPayload) {
  return apiRequest(ApisServiceApp.post(`${prefix(khachHangId)}/tao-dia-chi-nhan-hang`, toPayload(body)))
}

export function capNhatDiaChiNhanHang(
  khachHangId: KhachHangId,
  diaChiId: DiaChiNhanHangId,
  body: IDiaChiNhanHangPayload,
) {
  return apiRequest(
    ApisServiceApp.put(`${prefix(khachHangId)}/${diaChiId}/cap-nhat-dia-chi-nhan-hang`, toPayload(body)),
  )
}

export function xoaDiaChiNhanHang(khachHangId: KhachHangId, diaChiId: DiaChiNhanHangId) {
  return apiRequest(ApisServiceApp.delete(`${prefix(khachHangId)}/${diaChiId}/xoa-dia-chi-nhan-hang`))
}

